import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Lightbox from "@/components/Lightbox";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const HERO = "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/bfsyrbkw_cafe%20image%202024.jpg";

const CAFE_GALLERY = [
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/xv16g21c_Carpe%20Diem%206.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/3juiqlhl_Cafe%20Image%202.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/iw5as92p_1770372079151.webp",
  "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/6sq2f2c5_20250726_172327.webp",
];

const FOOD_GALLERY = [
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/0qmunbfk_3.%20Nut%20Monkey.jpg", caption: "Nut Monkey · waffle" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/1twxy3ph_Egg%20Sandwich.jpg", caption: "Eggstremely Basic · sandwich" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/xr9nc57h_IMG20241113120451.webp", caption: "Birdie's Buns · chicken burger + fries" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/nuz7xu9m_IMG20241114104950.webp", caption: "Caramel Cappuccino" },
  { url: "https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/3exl8v82_WMC.webp", caption: "Watermelon Mint Cooler" },
];


export default function Cafe() {
  const [lbIdx, setLbIdx] = useState(-1);
  const [sections, setSections] = useState([]);
  useEffect(() => {
    api.get("/menu").then(r => setSections(r.data.sections || [])).catch(() => setSections([]));
  }, []);
  return (
    <div data-testid="cafe-page">
      <section className="grid lg:grid-cols-12 gap-0 min-h-[80vh]">
        <div className="lg:col-span-7 order-2 lg:order-1 px-6 md:px-12 lg:px-20 py-16 flex flex-col justify-center">
          <div className="overline">04 — Cafe</div>
          <h1 className="display text-6xl md:text-8xl mt-6">Eat <em className="italic text-terra">slowly.</em></h1>
          <p className="mt-8 text-base text-muted2 max-w-md leading-relaxed">
            A small kitchen with a long memory. We cook with local produce, slow methods and seasonal whims.
            Sit indoors among the art, or out in the garden under the trees.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <div className="text-xs text-muted2 leading-relaxed">
              <div className="overline mb-1" style={{fontSize:'0.6rem'}}>Hours</div>
              Wed–Sun · 10 AM – 7 PM<br/>
              Cafe last orders 6:30 PM · Closed on public holidays
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-2 h-[60vh] lg:h-auto">
          <img src={HERO} alt="Cafe" className="w-full h-full object-cover"/>
        </div>
      </section>

      {/* Ambience gallery */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-24">
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 aspect-[16/10] overflow-hidden border border-soft">
            <img src={CAFE_GALLERY[0]} alt="Cafe interior" className="reveal-img w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="lg:col-span-4 aspect-[16/10] lg:aspect-auto overflow-hidden border border-soft">
            <img src={CAFE_GALLERY[1]} alt="Garden with Amor mural" className="reveal-img w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="lg:col-span-4 aspect-[4/3] overflow-hidden border border-soft lg:translate-y-6">
            <img src={CAFE_GALLERY[2]} alt="Garden dining" className="reveal-img w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="lg:col-span-8 aspect-[16/9] overflow-hidden border border-soft lg:translate-y-6">
            <img src={CAFE_GALLERY[3]} alt="Evening garden" className="reveal-img w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-6 md:px-12 pt-24 pb-16">
        <div className="overline text-center">Carpe Diem Art Cafe</div>
        <h2 className="display text-4xl md:text-5xl mt-6 text-center">The menu.</h2>
        <p className="text-center text-muted2 mt-3 text-sm">All prices in INR. Cafe last orders 6:30 PM.</p>

        {/* From the kitchen · tap-to-open gallery */}
        <div className="mt-14">
          <div className="flex items-end justify-between mb-4">
            <div className="overline">From the kitchen</div>
            <button
              type="button"
              onClick={() => setLbIdx(0)}
              className="inline-flex items-center gap-1.5 text-xs hover:text-terra transition"
              data-testid="cafe-food-gallery-open"
            >
              Tap any dish <ArrowUpRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2 md:gap-3">
            {FOOD_GALLERY.map((f, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLbIdx(i)}
                className="group relative aspect-square overflow-hidden border border-soft"
                data-testid={`cafe-food-thumb-${i}`}
                aria-label={f.caption}
              >
                <img src={f.url} alt={f.caption} className="reveal-img w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky anchor nav */}
      <nav
        className="sticky top-[72px] z-30 bg-[hsl(var(--background))]/85 backdrop-blur-xl border-y border-soft"
        data-testid="menu-anchor-nav"
      >
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 flex gap-1 md:gap-2 overflow-x-auto whitespace-nowrap py-2 scrollbar-none">
          {sections.map((sec) => {
            const id = sec.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <a
                key={id}
                href={`#${id}`}
                className="text-[11px] tracking-widest uppercase px-3 py-2 hover:text-terra hover:bg-cream-2 transition rounded-none"
                data-testid={`menu-anchor-${id}`}
              >
                {sec.name}
              </a>
            );
          })}
        </div>
      </nav>

      <section className="max-w-[1000px] mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="space-y-16">
          {sections.map((sec) => {
            const id = sec.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <div key={sec.name} id={id} style={{ scrollMarginTop: "140px" }} data-testid={`menu-section-${id}`}>
                <div className="overline mb-6">{sec.name}</div>
                <ul className="divide-y divide-[hsl(var(--border))]">
                  {sec.items.map((it) => (
                    <li key={it.id} className="py-4 flex items-baseline gap-6" data-testid={`menu-item-${it.id}`}>
                      <div className="flex-1">
                        <div className="font-serif text-2xl tracking-tight">{it.name}</div>
                        {it.description && <div className="text-sm text-muted2 mt-1 italic">{it.description}</div>}
                      </div>
                      <div className="font-serif text-xl">{it.price_inr}</div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-16 hairline pt-8 text-center text-xs text-muted2 max-w-2xl mx-auto leading-relaxed">
          All prices are in INR and were correct at the time of publishing. The café’s live menu and pricing evolve with the seasons — the version served in-house may differ from what you see here. For today’s specials, please ask your server.
        </div>
      </section>

      {lbIdx >= 0 && (
        <Lightbox
          images={FOOD_GALLERY}
          index={lbIdx}
          onIndex={setLbIdx}
          onClose={() => setLbIdx(-1)}
        />
      )}
    </div>
  );
}
